/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, InputNumber, Modal, Segmented, Typography } from "antd";
import dayjs from "dayjs";
import { PiPrinterFill } from "react-icons/pi";
import { TbRefresh } from "react-icons/tb";

import logo from "../../../assets/images/logo_nsf.png";
import GoodsReceiptService from "../../../service/ERP/GoodsReceipt.service";
import JuService from "../../../service/ERP/Ju.service";
import Barcode128 from "./Barcode128";
import "./gr-tags.css";

const grService = GoodsReceiptService();
const juService = JuService();

/* 1 ป้าย/หน้า */
const TAGS_PER_PAGE = 1;

/* ค่าตั้งต้นของป้าย — ผู้ใช้ปรับเองได้จากแผง "ตั้งค่าป้าย"
   pageW/pageH  = ขนาดหน้ากระดาษ (นิ้ว) ต้องตรงกับ stock ในไดรเวอร์เครื่องพิมพ์
   labelW/labelH= ขนาดตัวป้าย (นิ้ว) ปกติเล็กกว่าหน้ากระดาษเล็กน้อย
   offX/offY    = ขยับป้าย (มม.) ชดเชยเครื่องพิมพ์ที่วางตำแหน่งไม่ตรง
   rot          = หมุนฟอร์ม 0 / 90 องศา
   barH         = ความสูงบาร์โค้ด (มม.)
   fs           = สเกลตัวอักษร (1 = 100%) */
const DEFAULT_CFG = {
  pageW: 3.9,
  pageH: 2.4,
  labelW: 3.8,
  labelH: 2.3,
  offX: 0,
  offY: 0,
  rot: 0,
  barH: 30,
  fs: 100,
};

const CFG_KEY = "gr-tags-print-cfg-v3";

const loadCfg = () => {
  try {
    const raw = window.localStorage.getItem(CFG_KEY);
    if (!raw) return DEFAULT_CFG;
    return { ...DEFAULT_CFG, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_CFG;
  }
};

const saveCfg = (cfg) => {
  try {
    window.localStorage.setItem(CFG_KEY, JSON.stringify(cfg));
  } catch {
    /* โหมด private / โควตาเต็ม — ข้ามไป ไม่ต้องแจ้ง */
  }
};

const num = (v) => Number(v || 0);
const fdate = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "-");

/* สารก่อภูมิแพ้ : รหัสสินค้าขึ้นต้นด้วย IG2 */
const isAllergen = (tag) => {
  /* รหัสบางใบมีช่องว่างหรือพิมพ์เล็กปนมา จึงตัดช่องว่างและทำเป็นตัวใหญ่ก่อนเทียบ */
  const code = String(tag?.stcode || "").replace(/\s/g, "").toUpperCase();
  return code.startsWith("IG2");
};

function buildPages(tags) {
  const pages = [];
  for (let i = 0; i < tags.length; i += TAGS_PER_PAGE) {
    pages.push(tags.slice(i, i + TAGS_PER_PAGE));
  }
  return pages;
}

/* ป้ายแนวนอน — กว้างพอ จึงวางหัวป้ายแถวเดียว
   โลโก้ | ชื่อสินค้า | กล่องที่  แล้วข้อมูลด้านล่างเรียง 2 คอลัมน์ */
function TagLabel({ tag, grdate }) {
  return (
    <div className="tag-label">
      <div className="tag-head">
        <img src={logo} alt="" className="tag-logo" />
        <div className="tag-name" title={tag.stname || tag.stcode}>
          {tag.stname || tag.stcode}
        </div>
        <div className="tag-box">
          {tag.box_no}/{tag.box_total}
        </div>
      </div>

      <div className="tag-rows">
        <div className="tag-row">
          <span className="tag-key">รหัสสินค้า</span>
          <span className="tag-val">{tag.stcode || "-"}</span>
        </div>
        <div className="tag-row">
          <span className="tag-key">จำนวน</span>
          <span className="tag-val is-strong">
            {num(tag.qty).toLocaleString()} {tag.unit || ""}
          </span>
        </div>
        <div className="tag-row">
          <span className="tag-key">Lot No.</span>
          <span className="tag-val is-strong">{tag.lot_no}</span>
        </div>
        {/* Express Lot มาจากใบตรวจรับ (QC) แสดงเฉพาะใบที่บันทึกไว้ */}
        {tag.express_lot ? (
          <div className="tag-row">
            <span className="tag-key">Express</span>
            <span className="tag-val is-strong">{tag.express_lot}</span>
          </div>
        ) : null}
        <div className="tag-row">
          <span className="tag-key">ผลิต</span>
          <span className="tag-val">{fdate(tag.mfg_date)}</span>
        </div>
        {/* วันที่รับเข้า = วันที่ใบรับสินค้า (er_grmaster.grdate) */}
        <div className="tag-row">
          <span className="tag-key">รับเข้า</span>
          <span className="tag-val">{fdate(grdate)}</span>
        </div>
        <div className="tag-row">
          <span className="tag-key">หมดอายุ</span>
          <span className="tag-val is-expire">{fdate(tag.expire_date)}</span>
        </div>
      </div>

      <div className="tag-barcode">
        <Barcode128
          value={tag.barcode || tag.lot_no}
          height={30}
          moduleWidth={1.1}
          fontSize={7}
        />
      </div>

      {/* ใต้บาร์โค้ด : รหัสใบรับสินค้าตัวเล็ก (4pt)
          สินค้ารหัสขึ้นต้น IG2 = สารก่อภูมิแพ้ ให้ขึ้นคำว่า Allergen ตัวหนาตัวใหญ่กว่า */}
      <div className="tag-foot">
        {isAllergen(tag) ? <span className="tag-allergen">Allergen</span> : null}
        {/* <span className="tag-grcode">{tag.grcode || ""}</span> */}
      </div>
    </div>
  );
}

/* ช่องกรอกในส่วนขยาย "ขนาดป้าย" */
function Field({ label, value, onChange, step = 0.1, min, max, suffix }) {
  return (
    <div className="tags-field">
      <label>
        {label}
        {suffix ? ` (${suffix})` : ""}
      </label>
      <InputNumber
        size="small"
        style={{ width: "100%" }}
        value={value}
        step={step}
        min={min}
        max={max}
        onChange={(v) => onChange(v ?? 0)}
      />
    </div>
  );
}

/* source = "gr" (ค่าเดิม) หรือ "ju" — ใช้ฟอร์มป้ายเดียวกัน ต่างกันแค่ที่มาของข้อมูล */
export default function GrTagsPrintPreview({ source = "gr" }) {
  const { code } = useParams();

  const [gr, setGr] = useState({});
  const [tags, setTags] = useState([]);
  const [cfg, setCfg] = useState(loadCfg);
  const [openMore, setOpenMore] = useState(false);

  const setField = (key) => (v) => setCfg((s) => ({ ...s, [key]: v }));

  const handlePrint = () => window.print();

  const handleReset = () => setCfg(DEFAULT_CFG);

  /* จำค่าไว้ในเครื่อง ครั้งหน้าเปิดมาได้ค่าเดิม */
  useEffect(() => {
    saveCfg(cfg);
  }, [cfg]);

  /* ฉีดกฎขนาดกระดาษตั้งแต่เปิดหน้า (ไม่ใช่ตอนกดปุ่ม)
     เพราะถ้าผู้ใช้กด Ctrl+P เอง จะไม่มีกฎนี้ แล้วไปใช้ @page ของหน้า print อื่น
     ที่หลุดเป็น global มาในบันเดิล (เช่น A4) ทำให้ตัวอย่างขึ้นผิดขนาด/ผิดแนว

     ฉีดตอน mount = อยู่ท้ายสุดของ cascade จึงชนะกฎในไฟล์ CSS ที่โหลดมาก่อน
     และอัปเดตทุกครั้งที่ผู้ใช้เปลี่ยนขนาดในแผงตั้งค่า

     หมายเหตุ: ขนาดนี้ต้องมี stock ตรงกันในไดรเวอร์เครื่องพิมพ์ด้วย
     ไม่งั้น Chrome จะถอยไปใช้ Letter แล้วเนื้อหา 1 หน้าจะกินสติ๊กเกอร์หลายใบ */
  useEffect(() => {
    const STYLE_ID = "gr-tags-print-page";
    let style = document.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = STYLE_ID;
      style.media = "print";
      document.head.appendChild(style);
    }
    style.innerHTML = `@page { size: ${cfg.pageW}in ${cfg.pageH}in; margin: 0; }`;
  }, [cfg.pageW, cfg.pageH]);

  useEffect(() => {
    const htmlStyle = document.documentElement.style;
    const bodyStyle = document.body.style;
    const apply = (v) => {
      htmlStyle.setProperty("overflow", v, "important");
      bodyStyle.setProperty("overflow", v, "important");
    };
    apply("auto");
    const onBefore = () => apply("visible");
    const onAfter = () => apply("auto");
    window.addEventListener("beforeprint", onBefore);
    window.addEventListener("afterprint", onAfter);
    return () => {
      window.removeEventListener("beforeprint", onBefore);
      window.removeEventListener("afterprint", onAfter);
      const s = document.getElementById("gr-tags-print-page");
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    (source === "ju" ? juService : grService)
      .tagsGet(code)
      .then((res) => {
        const { gr: g, tags: t } = res.data?.data || {};
        setGr(g || {});
        setTags(t || []);
        if (!t || t.length === 0) {
          Modal.warning({
            title: "ยังไม่มี Tags",
            content:
              source === "ju"
                ? "เอกสารนี้ยังไม่มีรายการสินค้า"
                : "ใบรับสินค้านี้ยังไม่ได้แยก Tags กรุณากลับไปแยกและกดบันทึกก่อน",
            onOk: () => window.close(),
          });
        }
      })
      .catch((err) => {
        Modal.error({
          title: "Error",
          content: err.response?.data?.message || "โหลดข้อมูลไม่สำเร็จ",
          onOk: () => window.close(),
        });
      });
  }, [code, source]);

  const pages = useMemo(() => buildPages(tags), [tags]);

  /* ส่งค่าลง CSS variable — ทุกอย่างใน gr-tags.css อ้างตัวแปรชุดนี้ */
  const cssVars = {
    "--pg-w": `${cfg.pageW}in`,
    "--pg-h": `${cfg.pageH}in`,
    "--lb-w": `${cfg.labelW}in`,
    "--lb-h": `${cfg.labelH}in`,
    "--off-x": `${cfg.offX}mm`,
    "--off-y": `${cfg.offY}mm`,
    "--rot": `${cfg.rot}deg`,
    "--bar-h": `${cfg.barH}mm`,
    "--fs": cfg.fs / 100,
  };

  const mm = (inch) => (inch * 25.4).toFixed(1);

  return (
    <div className="tags-screen" id="gr-tags" style={cssVars}>
      <div className="tags-toolbar">
        <Button
          className="bn-center bg-blue-400"
          onClick={handlePrint}
          icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}
        >
          พิมพ์
        </Button>
      </div>

      <div className="tags-settings">
        {/* แถวหลัก : ขยับตำแหน่ง + หมุนฟอร์ม */}
        <div className="tags-bar">
          <div className="tags-bar-title">ปรับตำแหน่งพิมพ์</div>

          <div className="tags-bar-item">
            <span>แนวนอน</span>
            <InputNumber
              size="small"
              style={{ width: 90 }}
              value={cfg.offX}
              step={0.5}
              min={-30}
              max={30}
              addonAfter="mm"
              onChange={(v) => setField("offX")(v ?? 0)}
            />
          </div>

          <div className="tags-bar-item">
            <span>แนวตั้ง</span>
            <InputNumber
              size="small"
              style={{ width: 90 }}
              value={cfg.offY}
              step={0.5}
              min={-30}
              max={30}
              addonAfter="mm"
              onChange={(v) => setField("offY")(v ?? 0)}
            />
          </div>

          <div className="tags-bar-item">
            <span>หมุนฟอร์ม</span>
            <Segmented
              size="small"
              value={cfg.rot}
              onChange={(v) => setField("rot")(v)}
              options={[
                { label: "แนวนอน 0°", value: 0 },
                { label: "แนวตั้ง 90°", value: 90 },
              ]}
            />
          </div>
        </div>

        {/* แถวรอง : รีเซ็ต + คำอธิบาย */}
        <div className="tags-bar-2">
          <Button
            size="small"
            onClick={handleReset}
            icon={<TbRefresh style={{ fontSize: "1rem" }} />}
          >
            รีเซ็ต
          </Button>
          <span className="tags-hint">ตั้งครั้งเดียว ระบบจำให้เลย</span>
          <Typography.Link
            style={{ fontSize: 12 }}
            onClick={() => setOpenMore((v) => !v)}
          >
            {openMore ? "ซ่อนขนาดป้าย" : "ขนาดป้าย"}
          </Typography.Link>
        </div>

        {/* ส่วนขยาย : ขนาดกระดาษ/ป้าย ปกติตั้งครั้งเดียวตอนติดตั้ง */}
        {openMore ? (
          <div className="tags-more">
            <Field
              label="หน้ากระดาษ กว้าง"
              suffix="นิ้ว"
              value={cfg.pageW}
              onChange={setField("pageW")}
              min={0.5}
              max={12}
            />
            <Field
              label="หน้ากระดาษ สูง"
              suffix="นิ้ว"
              value={cfg.pageH}
              onChange={setField("pageH")}
              min={0.5}
              max={12}
            />
            <Field
              label="ตัวป้าย กว้าง"
              suffix="นิ้ว"
              value={cfg.labelW}
              onChange={setField("labelW")}
              min={0.5}
              max={12}
            />
            <Field
              label="ตัวป้าย สูง"
              suffix="นิ้ว"
              value={cfg.labelH}
              onChange={setField("labelH")}
              min={0.5}
              max={12}
            />
            <Field
              label="ความสูงบาร์โค้ด"
              suffix="มม."
              value={cfg.barH}
              onChange={setField("barH")}
              step={1}
              min={6}
              max={60}
            />
            <Field
              label="ขนาดตัวอักษร"
              suffix="%"
              value={cfg.fs}
              onChange={setField("fs")}
              step={5}
              min={50}
              max={160}
            />
            <div className="tags-more-note">
              ตั้ง custom stock ในไดรเวอร์เครื่องพิมพ์ให้เป็น{" "}
              <b>
                {mm(cfg.pageW)} x {mm(cfg.pageH)} มม.
              </b>{" "}
              ({cfg.pageW} x {cfg.pageH} นิ้ว) แล้วเลือกขนาดนี้ในหน้าต่าง Print
              ด้วย — ถ้าไดรเวอร์ไม่มีขนาดนี้ Chrome จะถอยไปใช้ Letter
              แล้วป้ายจะถูกหั่นข้ามสติ๊กเกอร์หลายใบ · ตอนพิมพ์ตั้ง Margins = None
              และ Scale = 100
              <br />
              <b>ขนาดหน้ากระดาษต้องเท่าสติ๊กเกอร์จริง</b>{" "}
              ถ้าตั้งสูงกว่าของจริง ส่วนที่เกินจะไหลไปสติ๊กเกอร์ใบถัดไป
              และท่อนล่างของป้ายจะหายตอนพิมพ์ — อยากให้ป้ายเตี้ยลง
              ให้ลด &quot;ตัวป้าย สูง&quot; ไม่ใช่เพิ่มความสูงหน้ากระดาษ
            </div>
          </div>
        ) : null}
      </div>

      <div className="tags-preview">
        {pages.map((pageTags, pageIndex) => (
          <div className="tags-page" key={`page-${pageIndex}`}>
            <div className="tags-grid">
              {pageTags.map((t) => (
                <TagLabel key={t.id ?? `${t.lot_no}-${t.box_no}`} tag={t} grdate={gr?.grdate} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
